/**
 * AdminInfluencersPage — /admin/influencers
 *
 * Admin cockpit for the creator program:
 *   • Leaderboard of active influencers (codes, signups, conversions,
 *     revenue, payout owed) sorted by revenue.
 *   • Promote/demote panel: search any user, flip role → 'influencer'
 *     (or back to 'user'). Uses existing PUT /api/admin/users/{id}/role.
 *   • Inline commission % editor per influencer.
 *   • "View payouts" jump to the per-creator payout ledger.
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../App';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Sparkles, ArrowLeft, UserPlus, UserMinus, DollarSign, Search, Download } from 'lucide-react';

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

/**
 * Trigger a browser download of an authenticated CSV blob. Uses axios
 * with `responseType: 'blob'` so the JWT header is honored and the
 * file lands in the user's Downloads folder without opening a tab.
 */
async function downloadCsvBlob(API, path, filename) {
  try {
    const r = await axios.get(`${API}${path}`, { responseType: 'blob' });
    const cd = r.headers['content-disposition'] || '';
    const m = /filename="?([^"]+)"?/.exec(cd);
    const name = m?.[1] || filename;
    const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
    toast.success(`Downloaded ${name}`);
  } catch (e) {
    toast.error(e?.response?.data?.detail || 'Download failed');
  }
}

export default function AdminInfluencersPage() {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [rows, setRows]     = useState([]);
  const [loading, setL]     = useState(true);
  const [payouts, setPayouts] = useState({}); // influencer_id -> payout data
  const [q, setQ]           = useState('');
  const [candidate, setCandidate] = useState(null); // user found by search

  const load = useCallback(async () => {
    setL(true);
    try {
      const { data } = await axios.get(`${API}/admin/influencers`);
      setRows(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load influencers');
    } finally { setL(false); }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  const searchUser = async () => {
    const query = q.trim().toLowerCase();
    if (!query) { setCandidate(null); return; }
    try {
      const { data } = await axios.get(`${API}/admin/users`);
      const list = Array.isArray(data) ? data : (data?.users || []);
      const hit = list.find((u) =>
        (u.email || '').toLowerCase().includes(query) ||
        (u.name  || '').toLowerCase().includes(query),
      );
      setCandidate(hit || null);
      if (!hit) toast.error('No user found for that query');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Search failed'); }
  };

  const setRole = async (userId, role) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role });
      toast.success(`Role updated to ${role}`);
      setCandidate((c) => c && c.id === userId ? { ...c, role } : c);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Role update failed'); }
  };

  const setCommission = async (userId, pct) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/commission-pct`, { commission_pct: pct });
      // Optimistic — the /admin/influencers row is stale until next fetch,
      // but the local edit stays.
      setRows((rs) => rs.map((r) => r.id === userId ? { ...r, commission_pct: pct, payout_owed: +(r.revenue * pct / 100).toFixed(2) } : r));
      toast.success('Commission updated');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const loadPayouts = async (userId) => {
    try {
      const { data } = await axios.get(`${API}/admin/influencers/${userId}/payouts`);
      setPayouts((p) => ({ ...p, [userId]: data }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Payouts load failed'); }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/portal')} className="mb-3">
          <ArrowLeft className="w-4 h-4 mr-1" /> Admin
        </Button>
        <div className="mb-6">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-primary mb-1">
            <Sparkles className="w-3 h-3" /> Creator Program
          </div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Influencer manager</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Promote users to influencer, tune their commission %, and watch the leaderboard.
          </p>
        </div>

        {/* Promote / demote panel */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <h2 className="font-heading font-bold mb-2">Promote / demote</h2>
            <div className="flex gap-2 mb-3">
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by email or name…"
                className="flex-1"
                data-testid="admin-inf-search-input"
                onKeyDown={(e) => { if (e.key === 'Enter') searchUser(); }}
              />
              <Button onClick={searchUser} data-testid="admin-inf-search-btn">
                <Search className="w-4 h-4 mr-1" /> Find user
              </Button>
            </div>
            {candidate && (
              <div className="flex items-center gap-3 rounded-md border border-border p-3" data-testid="admin-inf-candidate">
                <div className="flex-1">
                  <div className="text-sm font-semibold">{candidate.name || candidate.email}</div>
                  <div className="text-xs text-muted-foreground">{candidate.email} · role: <b>{candidate.role}</b></div>
                </div>
                {candidate.role === 'influencer' ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRole(candidate.id, 'user')}
                    data-testid="admin-inf-demote-btn"
                  >
                    <UserMinus className="w-4 h-4 mr-1" /> Remove influencer
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => setRole(candidate.id, 'influencer')}
                    data-testid="admin-inf-promote-btn"
                  >
                    <UserPlus className="w-4 h-4 mr-1" /> Make influencer
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Leaderboard */}
        <Card>
          <CardContent className="p-0">
            <div className="p-4 border-b border-border flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <h2 className="font-heading font-bold">Leaderboard</h2>
                <p className="text-xs text-muted-foreground">{rows.length} influencer{rows.length === 1 ? '' : 's'} sorted by attributed revenue.</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => downloadCsvBlob(API, '/admin/influencers/payouts.csv', 'fdacademy-payouts.csv')}
                data-testid="admin-inf-export-all-csv"
                title="Download every influencer's payout ledger as a single CSV, ready to hand to accounting."
                className="rounded-full font-bold"
              >
                <Download className="w-4 h-4 mr-1.5" />
                Export all payouts (CSV)
              </Button>
            </div>
            {loading ? (
              <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">No influencers yet. Promote a user above to get started.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-inf-leaderboard">
                  <thead className="border-b border-border bg-slate-50 dark:bg-slate-900">
                    <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                      <th className="px-4 py-2">#</th>
                      <th className="px-2 py-2">Creator</th>
                      <th className="px-2 py-2 text-right">Codes</th>
                      <th className="px-2 py-2 text-right">Signups</th>
                      <th className="px-2 py-2 text-right">Conversions</th>
                      <th className="px-2 py-2 text-right">Revenue</th>
                      <th className="px-2 py-2 text-right">Comm %</th>
                      <th className="px-2 py-2 text-right">Payout owed</th>
                      <th className="px-4 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={r.id} className="border-b border-border/60" data-testid={`admin-inf-row-${r.id}`}>
                        <td className="px-4 py-2 text-muted-foreground font-mono text-xs">{i + 1}</td>
                        <td className="px-2 py-2">
                          <div className="text-sm font-semibold">{r.name || r.email}</div>
                          <div className="text-[11px] text-muted-foreground">{r.email}</div>
                        </td>
                        <td className="px-2 py-2 text-right font-mono">{r.code_count}</td>
                        <td className="px-2 py-2 text-right font-mono">{r.signups}</td>
                        <td className="px-2 py-2 text-right font-mono">{r.conversions}</td>
                        <td className="px-2 py-2 text-right font-mono">{money(r.revenue)}</td>
                        <td className="px-2 py-2 text-right">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            defaultValue={r.commission_pct}
                            onBlur={(e) => {
                              const v = Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0));
                              if (v !== r.commission_pct) setCommission(r.id, v);
                            }}
                            className="w-14 h-7 px-1.5 text-xs rounded border border-input bg-background text-right"
                            data-testid={`admin-inf-commission-${r.id}`}
                          />
                          <span className="text-xs text-muted-foreground ml-0.5">%</span>
                        </td>
                        <td className="px-2 py-2 text-right font-mono text-emerald-700 font-bold">{money(r.payout_owed)}</td>
                        <td className="px-4 py-2 text-right">
                          <Button size="sm" variant="ghost" onClick={() => loadPayouts(r.id)} title="View payout history">
                            <DollarSign className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => downloadCsvBlob(API, `/admin/influencers/${r.id}/payouts.csv`, `payouts-${r.email}.csv`)}
                            title="Download this creator's payout ledger as CSV"
                            data-testid={`admin-inf-export-csv-${r.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Expanded payouts */}
                {Object.entries(payouts).map(([uid, p]) => (
                  <div key={uid} className="p-4 border-t border-border bg-slate-50/60 dark:bg-slate-900/40" data-testid={`admin-inf-payouts-${uid}`}>
                    <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                      {p.user?.name || p.user?.email} — payout ledger ({p.commission_pct}% commission)
                    </div>
                    {p.periods?.length === 0 ? (
                      <div className="text-xs text-muted-foreground">No paid conversions yet.</div>
                    ) : (
                      <table className="w-full text-xs">
                        <thead className="text-[10px] uppercase tracking-widest text-muted-foreground">
                          <tr className="text-left">
                            <th className="pb-1">Period</th>
                            <th className="pb-1 text-right">Conversions</th>
                            <th className="pb-1 text-right">Revenue</th>
                            <th className="pb-1 text-right">Payout</th>
                          </tr>
                        </thead>
                        <tbody>
                          {p.periods.map((row) => (
                            <tr key={row.period} className="border-t border-border/40">
                              <td className="py-1 font-mono">{row.period}</td>
                              <td className="py-1 text-right font-mono">{row.conversions}</td>
                              <td className="py-1 text-right font-mono">{money(row.revenue)}</td>
                              <td className="py-1 text-right font-mono text-emerald-700 font-bold">{money(row.payout)}</td>
                            </tr>
                          ))}
                          <tr className="border-t border-border font-bold">
                            <td className="py-1">Totals</td>
                            <td className="py-1 text-right font-mono">{p.totals.conversions}</td>
                            <td className="py-1 text-right font-mono">{money(p.totals.revenue)}</td>
                            <td className="py-1 text-right font-mono text-emerald-700">{money(p.totals.payout)}</td>
                          </tr>
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
